import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { AnalyticsHooks, AnalyticsUtils } from '@frontend/analytics-shared';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { generateTrackingId } from '@frontend/call-intel-core';
import { formatDate } from '@frontend/date';
import { largeDownloadsRef } from '@frontend/file-download-helper';
import { i18next, Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMutation } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  ChecklistMenuField,
  Chip,
  DateRangeField,
  Modal,
  ModalControlModalProps,
  Text,
  useAlert,
  useFormField,
} from '@frontend/design-system';
import { useCallIntelLocations } from './hooks/use-call-intel-locations';

const CSV_EXPORT_MODAL_TIME_PERIODS = {
  custom: {
    label: i18next.t('Custom', { ns: 'analytics' }),
    value: {
      startDate: '',
      endDate: '',
    },
  },
  today: {
    label: i18next.t('Today', { ns: 'analytics' }),
    value: AnalyticsUtils.findDatesRange({ range: 'day' }),
  },
  yesterday: {
    label: i18next.t('Yesterday', { ns: 'analytics' }),
    value: AnalyticsUtils.findDatesRange({ offset: 1, range: 'day' }),
  },
  last7Days: {
    label: i18next.t('Last 7 Days', { ns: 'analytics' }),
    value: AnalyticsUtils.findDatesRange({ offset: 7, range: 'day' }),
  },
  last14Days: {
    label: i18next.t('Last 14 Days', { ns: 'analytics' }),
    value: AnalyticsUtils.findDatesRange({ offset: 14, range: 'day' }),
  },
  last30Days: {
    label: i18next.t('Last 30 Days', { ns: 'analytics' }),
    value: AnalyticsUtils.findDatesRange({ offset: 30, range: 'day' }),
  },
};

const DISPLAY_DATE_FORMAT = 'ddd MMM D, YYYY';
const MAX_DATE_RANGE_DAYS = 30;
const MAX_LOCATION_SELECTION = 5;

const INITIAL_DATE_STATE = {
  startDate: '',
  endDate: '',
  selectedPeriod: '',
};

type CallRecordsExportModalProps = ModalControlModalProps & { isDemo?: boolean };

export const CallRecordsExportModal = (props: CallRecordsExportModalProps) => {
  const { t } = useTranslation('analytics');
  const alerts = useAlert();
  const weaveUser = isWeaveUser();

  const { isMultiLocation, locations } = useCallIntelLocations();

  const [showCustomDateRangeSelector, setShowCustomDateRangeSelector] = useState(false);
  const [selectedDates, setSelectedDates] = useState(INITIAL_DATE_STATE);
  const [timePeriodValues, setTimePeriodValues] = useState({
    endDate: '',
    selectedPeriod: null as keyof typeof CSV_EXPORT_MODAL_TIME_PERIODS | null,
    startDate: '',
  });

  const locationFormField = useFormField({
    type: 'checklist',
    value: [],
  });

  const customDateRangeField = useFormField(
    {
      minDate: dayjs('2024-01-01').startOf('day').toISOString(), // As call intel data starts from Jan 1, 2024
      maxDate: AnalyticsUtils.defaultDateRangeMap['today'].endDate,
      type: 'dateRange',
      value: [],
      validator: (dateRangeField) => {
        const value = dateRangeField.value;

        if (!value[0]) {
          return '';
        }

        if (value[0] && value[1]) {
          const startDate = dayjs(value[0]);
          const endDate = dayjs(value[1]);

          // Check if end date is after start date
          if (endDate.isBefore(startDate)) {
            return t('End date must be after the start date');
          }

          // Check for maximum date range
          const daysDifference = endDate.diff(startDate, 'days');
          if (daysDifference > MAX_DATE_RANGE_DAYS) {
            return t('Date Range must be {{maxRange}} days or less', {
              maxRange: MAX_DATE_RANGE_DAYS + 1,
            });
          }
        }

        return '';
      },
    },
    []
  );

  const accessibleLocationsMap = useMemo(() => {
    return Object.entries(locations).reduce((acc, [locationId, locationName]) => {
      let hasAccess: boolean;

      if (props?.isDemo) {
        hasAccess = true;
      } else if (weaveUser) {
        hasAccess = true;
      } else {
        hasAccess = hasSchemaACL(locationId, Permission.CALL_CLASSIFICATION_READ);
      }

      if (hasAccess) {
        acc[locationId] = locationName;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [props?.isDemo, locations, weaveUser]);

  const { TimePeriodDropDownSelector } = AnalyticsHooks.useTimePeriodDropDownSelector<
    typeof CSV_EXPORT_MODAL_TIME_PERIODS
  >({
    defaultPeriod: null,
    skipFindingExistingPeriod: true,
    timePeriods: CSV_EXPORT_MODAL_TIME_PERIODS,
    values: timePeriodValues,
  });

  const isValidDateRange = useMemo(() => {
    if (showCustomDateRangeSelector) {
      const hasValidDates = customDateRangeField.value.length > 0 && customDateRangeField.value.every((date) => !!date);
      const hasValidationErrors = customDateRangeField.error && customDateRangeField.error.length > 0;

      return hasValidDates && !hasValidationErrors;
    }
    return !!selectedDates.startDate && !!selectedDates.endDate;
  }, [
    showCustomDateRangeSelector,
    selectedDates.startDate,
    selectedDates.endDate,
    customDateRangeField.value,
    customDateRangeField.error,
  ]);

  const dateRangeProps = useMemo(() => {
    return {
      startDate: selectedDates.startDate || customDateRangeField.value[0],
      endDate: selectedDates.endDate || customDateRangeField.value[1],
    };
  }, [customDateRangeField.value, selectedDates.endDate, selectedDates.startDate]);

  const isValidLocationSelection =
    Object.keys(accessibleLocationsMap).length === 1 || locationFormField.value.length > 0;

  const isDownloadDisabled = !isValidDateRange || !isValidLocationSelection;

  const renderLocationChips = useCallback(
    (props: { selectedKeys: string[]; selectedLabels: string[]; keyToLabelMap: Record<string, string> }) => (
      <ChecklistMenuField.ChipInput
        {...props}
        variant='primaryDark'
        onRemoveClick={(id) => {
          locationFormField.onChange?.({
            name: 'locationIds',
            value: [id],
          });
        }}
      />
    ),
    [locationFormField]
  );

  const handleTimePeriodChange = useCallback(
    ({
      endDate,
      startDate,
      selectedPeriod,
    }: {
      endDate?: string;
      startDate?: string;
      selectedPeriod?: keyof typeof CSV_EXPORT_MODAL_TIME_PERIODS | null;
    }) => {
      setTimePeriodValues({
        endDate: endDate || '',
        selectedPeriod: selectedPeriod || null,
        startDate: startDate || '',
      });
      if (selectedPeriod === 'custom') {
        setShowCustomDateRangeSelector(true);
        setSelectedDates({ startDate: '', endDate: '', selectedPeriod: selectedPeriod as string });
        customDateRangeField.onChange?.({
          name: 'customDateRange',
          value: [],
        });
      } else if (startDate && endDate && selectedPeriod) {
        setShowCustomDateRangeSelector(false);
        setSelectedDates({ startDate, endDate, selectedPeriod: selectedPeriod });
      }
    },
    []
  );

  const resetModalState = useCallback(() => {
    setShowCustomDateRangeSelector(false);
    setSelectedDates(INITIAL_DATE_STATE);

    setTimePeriodValues({
      endDate: '',
      selectedPeriod: null,
      startDate: '',
    });

    locationFormField.onChange?.({
      name: 'days',
      value: [],
    });

    customDateRangeField.onChange?.({
      name: 'customDateRange',
      value: [],
    });
  }, [customDateRangeField, locationFormField]);

  const handleModalClose = useCallback(() => {
    resetModalState();
    props.onClose();
  }, [props, resetModalState]);

  const { mutate } = useMutation({
    mutationFn: async ({
      endTime,
      locationIds,
      startTime,
    }: {
      endTime: string;
      locationIds: string[];
      startTime: string;
    }) => {
      const response = await PhoneCallsApi.exportCallRecords({
        endTime,
        includeCallIntel: true,
        locationIds,
        startTime,
      });

      return {
        ...response,
        endTime,
        startTime,
      };
    },
    onSuccess: ({ exportId, startTime, endTime }) => {
      const formattedStartDate = formatDate(new Date(startTime), 'MMM D, YYYY');
      const formattedEndDate = formatDate(new Date(endTime), 'MMM D, YYYY');

      largeDownloadsRef.current?.addEventInProgress({
        fileExtension: 'csv',
        fileName: `call-intelligence-${formattedStartDate}-${formattedEndDate}`,
        id: exportId,
        url: `/phone-exp/phone-records/v1/export/call-records/${exportId}/status`,
      });
      handleModalClose();
    },
    onError: (error) => {
      alerts.error(t('An error occurred while preparing your download. Please try again later.'));
      sentry.warn({
        team: 'analytics',
        topic: 'call-intelligence',
        error: `An error occurred while preparing the csv export download from call-records-table.: ${error}`,
      });
    },
  });

  const handleExportTrigger = useCallback(() => {
    let exportStartTime: string;
    let exportEndTime: string;

    if (showCustomDateRangeSelector && customDateRangeField.value.length === 2) {
      const [startDate, endDate] = customDateRangeField.value;
      const startDateTime = dayjs(startDate);
      const endDateTime = dayjs(endDate);
      exportStartTime = startDateTime.startOf('day').toISOString();
      exportEndTime = endDateTime.endOf('day').toISOString();
    } else if (selectedDates.startDate && selectedDates.endDate) {
      exportStartTime = new Date(selectedDates.startDate).toISOString();
      exportEndTime = new Date(selectedDates.endDate).toISOString();
    } else {
      alerts.error(t('Please select a valid date range before downloading.'));
      return;
    }

    const locationIds =
      Object.keys(accessibleLocationsMap).length === 1 ? Object.keys(accessibleLocationsMap) : locationFormField.value;

    mutate({
      endTime: exportEndTime,
      locationIds,
      startTime: exportStartTime,
    });
  }, [
    showCustomDateRangeSelector,
    customDateRangeField.value,
    selectedDates.startDate,
    selectedDates.endDate,
    locationFormField.value,
    accessibleLocationsMap,
    mutate,
    alerts,
    t,
  ]);

  const isLocationSelectionAtLimit = useCallback(
    (locationId: string) => {
      const currentSelections = locationFormField.value;
      const isAlreadySelected = currentSelections.includes(locationId);
      const hasReachedMaxLimit = currentSelections.length >= MAX_LOCATION_SELECTION;

      // Don't disable if already selected (allows deselection) or hasn't reached limit
      return !isAlreadySelected && hasReachedMaxLimit;
    },
    [locationFormField.value]
  );

  return (
    <Modal {...props} onClose={handleModalClose} maxWidth={600}>
      <Modal.Header onClose={handleModalClose}>{t('Download Data to CSV')}</Modal.Header>
      <Modal.Body>
        <BannerNotification
          css={styles.bannerNotification}
          status='info'
          message={
            <Trans t={t}>
              <Text weight='bold' size='medium'>
                Know Before Downloading:
              </Text>
              <ul>
                <li>
                  <Text size='medium'>
                    Filters applied to the data table{' '}
                    <Text as='span' weight='bold' size='medium'>
                      will not
                    </Text>{' '}
                    transfer to the exported file.
                  </Text>
                </li>
                <li>
                  <Text size='medium'>
                    The export will include{' '}
                    <Text as='span' weight='bold' size='medium'>
                      all calls
                    </Text>
                    {', '}
                    including{' '}
                    <Text as='span' weight='bold' size='medium'>
                      missed
                    </Text>{' '}
                    and{' '}
                    <Text as='span' weight='bold' size='medium'>
                      abandoned
                    </Text>{' '}
                    calls. To view only Call Intelligence data in your CSV, filter for{' '}
                    <Text as='span' weight='bold' size='medium'>
                      analyzed
                    </Text>{' '}
                    call in the{' '}
                    <Text as='span' size='medium' weight='bold'>
                      call_intel_analysis_status
                    </Text>{' '}
                    column
                  </Text>
                </li>
              </ul>
            </Trans>
          }
        />
        <Text css={styles.topMargin}>{t('Please select your desired criteria for your downloaded CSV file.')}</Text>
        <div css={styles.mainContainer}>
          <div css={styles.fieldContainer}>
            <Text weight='bold'>{t('Time Period')}</Text>
            <TimePeriodDropDownSelector
              label={t('Select Time Period')}
              placeholder={t('Select Time Period')}
              onChange={handleTimePeriodChange}
              showDropdownIcon
              trackingIdBase={generateTrackingId({
                component: 'call-records-export-modal',
                context: 'time-period-selector',
              })}
            />
            {showCustomDateRangeSelector && (
              <div>
                <DateRangeField
                  {...customDateRangeField}
                  label={t('Time Range Date')}
                  name='customDateRange'
                  css={[!!customDateRangeField.error && styles.dateRangeFieldError]}
                />
                {!!showCustomDateRangeSelector && (
                  <Text size='small' color='error' css={{ paddingTop: theme.spacing(1) }}>
                    {customDateRangeField.error}
                  </Text>
                )}
              </div>
            )}

            {!showCustomDateRangeSelector && <DateRangeDisplay {...dateRangeProps} />}
          </div>

          {isMultiLocation && (
            <div css={styles.fieldContainer}>
              <Text weight='bold'>{t('Locations')}</Text>
              <ChecklistMenuField
                {...locationFormField}
                label={t('Select Location(s)')}
                placeholder={t('Select Location(s)')}
                name='locationIds'
                customRender={renderLocationChips}
                floatingStyleOverrides={{ width: '100%', maxWidth: '100%' }}
                showSelectionBar={false}
              >
                {Object.entries(accessibleLocationsMap).map(([locationId, locationName]) => (
                  <ChecklistMenuField.Option
                    key={locationId}
                    value={locationId}
                    disabled={isLocationSelectionAtLimit(locationId)}
                    disabledMessage={t('You have reached the maximum number of locations')}
                  >
                    {locationName}
                  </ChecklistMenuField.Option>
                ))}
              </ChecklistMenuField>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          disabled: isDownloadDisabled,
          label: t('Download CSV'),
          onClick: handleExportTrigger,
          trackingId: generateTrackingId({
            component: 'call-records-export-modal',
            context: 'download-csv-button',
          }),
        }}
        secondary={{
          label: t('Cancel'),
          onClick: handleModalClose,
          trackingId: generateTrackingId({
            component: 'call-records-export-modal',
            context: 'cancel-button',
          }),
        }}
      />
    </Modal>
  );
};

const DateRangeDisplay = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const { t } = useTranslation('analytics');

  if (!startDate || !endDate) return null;
  const startTime = formatDate(new Date(startDate), DISPLAY_DATE_FORMAT);
  const endTime = formatDate(new Date(endDate), DISPLAY_DATE_FORMAT);

  return (
    <Text css={styles.dateRangeContainer}>
      <Text as='span' size='medium' color='subdued' css={styles.noWrap}>
        {t('Date Range:')}
      </Text>
      <div css={styles.dateRangeContainer}>
        <Chip variant='default' leftElement={<Icon name='calendar' size={14} />} css={styles.dateChip}>
          {startTime}
        </Chip>
        <Text as='span' size='medium' color='subdued'>
          {t('to')}
        </Text>
        <Chip variant='default' leftElement={<Icon name='calendar' size={14} />} css={styles.dateChip}>
          {endTime}
        </Chip>
      </div>
    </Text>
  );
};

const styles = {
  bannerNotification: css`
    border-left: 6px solid ${theme.colors.primary50};
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(1)};
  `,

  topMargin: css`
    margin-top: ${theme.spacing(2)};
  `,

  mainContainer: css`
    display: flex;
    gap: ${theme.spacing(4)};
    flex-direction: column;
  `,

  fieldContainer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex-direction: column;
  `,

  dateRangeContainer: css`
    align-items: center;
    display: inline-flex;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
  `,

  dateChip: css`
    width: 100%;
    color: ${theme.colors.black};
    max-width: fit-content;
  `,

  // Since DateRangeField doesn't support error state, we need custom styling
  dateRangeFieldError: css`
    label {
      color: ${theme.colors.critical50};
    }
    div > div > div {
      border-color: ${theme.colors.critical50};
    }
  `,
  noWrap: css`
    white-space: nowrap;
  `,
};
